"use client"

import { useState, useEffect } from "react"
import { apiClient } from "../services/apiClient"
import { useAuth } from "../contexts/AuthContext"
import { useLanguage } from "../contexts/LanguageContext"
import { Icons } from "./Icons"

export function AdminDashboard() {
  const { user } = useAuth()
  const { t, language } = useLanguage()

  const [users, setUsers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      const response = await apiClient.getAllUsers()
      setUsers(response.users)
    } catch (err) {
      setError(err.message || "Error loading users")
    } finally {
      setIsLoading(false)
    }
  }

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(t("common.deleteConfirmText"))) {
      return
    }

    try {
      await apiClient.deleteUser(userId)
      setError("")
      alert(t("messages.userDeleted"))
      await loadUsers()
    } catch (err) {
      setError(err.message || t("messages.deleteError"))
    }
  }

  const resetPassword = async (userId, userName) => {
    if (!window.confirm(`${t("common.resetPassword")} для ${userName}?`)) {
      return
    }

    try {
      const response = await apiClient.resetUserPassword(userId, language)
      setError("")
      alert(response.message || t("messages.passwordReset"))
    } catch (err) {
      setError(err.message || t("messages.resetError"))
    }
  }

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await apiClient.updateUserStatus(userId, !currentStatus)
      await loadUsers()
    } catch (err) {
      setError(err.message || "Error changing status")
    }
  }

  const getRoleName = (role) => {
    return t(`role.${role}`)
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <Icons.Admin className="w-5 h-5 text-red-600" />
      case "teacher":
        return <Icons.Teacher className="w-5 h-5 text-green-600" />
      case "student":
        return <Icons.Student className="w-5 h-5 text-blue-600" />
      default:
        return <Icons.User className="w-5 h-5 text-gray-600" />
    }
  }

  const stats = {
    total: users.length,
    admins: users.filter((u) => u.role === "admin").length,
    teachers: users.filter((u) => u.role === "teacher").length,
    students: users.filter((u) => u.role === "student").length,
    active: users.filter((u) => u.is_active).length,
  }

  if (isLoading) {
    return (
      <div className="text-center p-8">
        <Icons.Spinner className="w-8 h-8 mx-auto mb-4" />
        <p>{t("common.loading")}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold flex items-center space-x-3">
          <Icons.Admin className="w-8 h-8 text-red-600" />
          <span>{t("admin.title")}</span>
        </h1>
        <button className="btn btn-primary flex items-center space-x-2" onClick={() => setIsCreateModalOpen(true)}>
          <Icons.Plus className="w-4 h-4" />
          <span>{t("admin.createTeacher")}</span>
        </button>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-5 gap-4">
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm text-gray-600">{t("common.total")}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.Admin className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <p className="text-sm text-gray-600">{t("admin.stats.admins")}</p>
              <p className="text-2xl font-bold text-red-600">{stats.admins}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.Teacher className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p className="text-sm text-gray-600">{t("admin.stats.teachers")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.teachers}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.Student className="w-6 h-6 mx-auto mb-2 text-purple-600" />
              <p className="text-sm text-gray-600">{t("admin.stats.students")}</p>
              <p className="text-2xl font-bold text-purple-600">{stats.students}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p className="text-sm text-gray-600">{t("common.active")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Список пользователей */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center space-x-2">
            <Icons.Users className="w-5 h-5" />
            <span>{t("admin.systemUsers")}</span>
          </h2>
          <p className="card-description">{t("admin.userManagement")}</p>
        </div>
        <div className="card-content">
          <div className="space-y-4">
            {users.map((user_n) => (
              <div key={user_n.id} className="justify-between items-center p-4 border border-rounded">
                <div className="grid grid-cols-5 space-x-4">
                  <div className="flex space-x-2">
                    <div>
                      {getRoleIcon(user_n.role)}
                    </div>
                    <div>
                      <p className="font-medium">{user_n.full_name}</p>
                      <p className="text-sm text-gray-600">{user_n.email}</p>
                    </div>
                  </div>
                  <div>
                    <span
                      className={`badge ${user_n.role === "admin"
                        ? "badge-danger"
                        : user_n.role === "teacher"
                          ? "badge-default"
                          : "badge-warning"
                        }`}
                    >
                      {getRoleName(user_n.role)}
                    </span>
                  </div>
                  <div>
                    <span
                      className={`badge ${user_n.is_active ? "badge-success" : "badge-danger"} flex items-center space-x-1`}
                    >
                      {user_n.is_active ? (
                        <Icons.CheckCircle className="w-3 h-3" />
                      ) : (
                        <Icons.XCircle className="w-3 h-3" />
                      )}
                      <span>{user_n.is_active ? t("common.active") : t("common.blocked")}</span>
                    </span>
                  </div>
                  <div>
                    <span className="badge badge-sm">
                      {t("nav.visits")}:{` `}
                      {`${user_n.visits}`}<br />
                      {t("nav.last_entry")}:<br />
                      {`${user_n.last_session && new Date(user_n.last_session).toLocaleString(language) || t("nav.never")}`}
                    </span>
                  </div>
                  {user_n.email !== user.email && (
                    <div className="grid gap-4">
                      <button
                        className={`btn btn-sm ${user_n.is_active ? "btn-danger" : "btn-primary"} flex items-center space-x-1`}
                        onClick={() => toggleUserStatus(user_n.id, user_n.is_active)}
                      >
                        {user_n.is_active ? (
                          <>
                            <Icons.Lock className="w-3 h-3" />
                            <span>{t("admin.block")}</span>
                          </>
                        ) : (
                          <>
                            <Icons.Unlock className="w-3 h-3" />
                            <span>{t("admin.unblock")}</span>
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary flex items-center space-x-1"
                        onClick={() => resetPassword(user_n.id, user_n.full_name)}
                      >
                        <Icons.Key className="w-3 h-3" />
                        <span>{t("common.resetPassword")}</span>
                      </button>
                      <button
                        className="btn btn-sm btn-danger flex items-center space-x-1"
                        onClick={() => deleteUser(user_n.id, user_n.full_name)}
                      >
                        <Icons.Delete className="w-3 h-3" />
                        <span>{t("common.deleteUser")}</span>
                      </button>
                    </div>
                  ) || (<div>{` `}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Модальное окно создания преподавателя */}
      {isCreateModalOpen && (
        <CreateTeacherModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false)
            loadUsers()
          }}
        />
      )}
    </div>
  )
}

function CreateTeacherModal({ onClose, onSuccess }) {
  const { t, language } = useLanguage()
  const [email, setEmail] = useState("")
  const [fullName, setFullName] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess("")
    setIsLoading(true)

    try {
      const response = await apiClient.createTeacher(email, fullName, language)
      setSuccess(response.message || t("createTeacher.success"))
      setEmail("")
      setFullName("")

      setTimeout(() => {
        onSuccess()
      }, 1500)
    } catch (err) {
      setError(err.message || t("createTeacher.error"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2 className="text-xl font-semibold mb-4 flex items-center space-x-2">
          <Icons.Teacher className="w-6 h-6 text-green-600" />
          <span>{t("createTeacher.title")}</span>
        </h2>
        <p className="text-gray-600 mb-6">{t("createTeacher.description")}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="email">
              {t("auth.email")}
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("createTeacher.emailPlaceholder")}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="fullName">
              {t("createTeacher.fullName")}
            </label>
            <input
              id="fullName"
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t("createTeacher.fullNamePlaceholder")}
              required
            />
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <div className="flex space-x-2">
            <button
              type="submit"
              className="btn btn-primary flex-1 flex items-center justify-center space-x-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Icons.Spinner className="w-4 h-4" />
                  <span>{t("createTeacher.creating")}</span>
                </>
              ) : (
                <>
                  <Icons.Plus className="w-4 h-4" />
                  <span>{t("common.create")}</span>
                </>
              )}
            </button>
            <button type="button" className="btn btn-outline" onClick={onClose}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
